import React from 'react';
import { useTranslation } from 'react-i18next';
import { getUserPlaceholderImage } from '../../../utils/imageUtils';

const StoryHeader = ({
    user,
    timestamp,
    onClose,
    onDelete,
    onReport,
    isOwnStory,
    isMuted,
    onToggleMute,
    hasReported,
    t
}) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const menuRef = React.useRef(null);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div
            style={{
                position: 'absolute',
                top: '24px',
                left: '0',
                right: '0',
                zIndex: 1002,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                pointerEvents: 'none' // Let clicks pass through, children enable pointer-events
            }}
        >
            {/* User Info */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    // Optional: navigate to profile
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}
                >
                    <img
                        src={user?.avatar || getUserPlaceholderImage(user)}
                        alt={user?.name || 'User'}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                        onError={(e) => {
                            e.target.src = getUserPlaceholderImage(user);
                        }}
                    />
                </div>
                <div style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                    <div
                        style={{
                            color: '#FFFFFF',
                            fontSize: '15px',
                            fontWeight: '600',
                            fontFamily: '"Red Hat Display", sans-serif',
                            lineHeight: '1.2'
                        }}
                    >
                        {user?.name || 'User'}
                    </div>
                    <div
                        style={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '13px',
                            fontFamily: '"Red Hat Display", sans-serif',
                        }}
                    >
                        {timestamp || 'Just now'}
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    pointerEvents: 'auto'
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Mute/Unmute Button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleMute();
                    }}
                    style={{
                        all: 'unset',
                        padding: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        backdropFilter: 'blur(4px)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s',
                    }}
                    aria-label={isMuted ? t('common.unmute') : t('common.mute')}
                >
                    {isMuted ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                            <line x1="23" y1="9" x2="17" y2="15"></line>
                            <line x1="17" y1="9" x2="23" y2="15"></line>
                        </svg>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                        </svg>
                    )}
                </button>

                {/* Unified Menu Button */}
                <div style={{ position: 'relative' }} ref={menuRef}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(!showMenu);
                        }}
                        style={{
                            all: 'unset',
                            padding: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            backdropFilter: 'blur(4px)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s',
                        }}
                        aria-label="More options"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#FFFFFF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="1" />
                            <circle cx="12" cy="19" r="1" />
                            <circle cx="12" cy="5" r="1" />
                        </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {showMenu && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '44px',
                                right: '0',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: '12px',
                                padding: '8px',
                                minWidth: '160px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                zIndex: 1100
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {isOwnStory ? (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete();
                                        setShowMenu(false);
                                    }}
                                    style={{
                                        all: 'unset',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        color: '#ff4d4f',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        transition: 'background-color 0.2s',
                                        zIndex: 1101
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 77, 79, 0.1)'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                    {t('stories.deleteStory')}
                                </button>
                            ) : (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!hasReported) {
                                            onReport();
                                        }
                                        setShowMenu(false);
                                    }}
                                    disabled={hasReported}
                                    style={{
                                        all: 'unset',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        cursor: hasReported ? 'default' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        color: hasReported ? '#999' : '#333',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        transition: 'background-color 0.2s',
                                        zIndex: 1101,
                                        opacity: hasReported ? 0.7 : 1
                                    }}
                                    onMouseEnter={(e) => !hasReported && (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)')}
                                    onMouseLeave={(e) => !hasReported && (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="8" x2="12" y2="12"></line>
                                        <circle cx="12" cy="16" r="0.8" fill="currentColor" stroke="none" />
                                    </svg>
                                    {hasReported ? t('stories.alreadyReported') || 'Already Reported' : t('stories.reportStory')}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Close Button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    style={{
                        all: 'unset',
                        padding: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(4px)',
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s',
                    }}
                    aria-label="Close"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default StoryHeader;
